import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { getCurrentQueryParams, getCurrentUrl } from './SearchBarUtil';
import { search, SearchResponse, SearchResult } from '../../search';
import SearchIcon from '../../svg/search.svg';
import LoadingSpinner from '../Generics/LoadingSpinner';

import CloseIcon from '../Icons/CloseIcon';

import './Search.scss';
import { translateLanguage } from '../utils/UtilityFunctions';
import { LunrQueryOpts } from 'src/lunr-query';

export interface OnSearchRes {
  searchTerm: string;
  res: SearchResult[];
  currentPage: number;
  totalResults: number;
}

export interface SearchBarProps {
  onSearch: (arg: OnSearchRes) => void;
  language: string;
  limit: number;
  typeFilter?: string;
}

function runSearch(
  searchTerm: string,
  language: string,
  currentPage: number,
  pageSize: number,
  typeFilter?: string
): Promise<SearchResponse> {
  if (searchTerm) {
    const query: LunrQueryOpts[] = [{ query: searchTerm, fuzzy: 2, required: true }];

    if (typeFilter && typeFilter !== 'All') {
      query.push({ field: 'nodeType', query: typeFilter, required: true });
    }

    return search(language, query, { pageSize, currentPage }).then((res) => {
      const url = new URL(getCurrentUrl());

      url.searchParams.set('search', searchTerm);
      url.searchParams.set('page', currentPage.toString());

      history.pushState({}, 'test', url.toString());

      return res;
    });
  } else {
    return Promise.resolve({
      results: [],
      totalResults: 0,
      jobNumber: 0,
    });
  }
}

export default function SearchBar({ onSearch, language, limit, typeFilter }: SearchBarProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, isLoading] = useState<boolean>(false);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      isLoading(true);

      runSearch(searchTerm, language, 1, limit, typeFilter).then((res) => {
        isLoading(false);
        onSearch({ searchTerm, res: res.results, currentPage: 1, totalResults: res.totalResults });
      });
    },
    [searchTerm]
  );

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value || '');
  }, []);

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(getCurrentQueryParams());
    const searchParam = queryParams.get('search') || '';
    const pageParam = queryParams.get('page');
    const currentPage = pageParam ? Number(pageParam) : 1;

    setSearchTerm(searchParam);

    isLoading(true);

    runSearch(searchParam, language, currentPage, limit, typeFilter).then((res) => {
      onSearch({
        searchTerm: searchParam,
        res: res.results,
        currentPage,
        totalResults: res.totalResults,
      });

      isLoading(false);
    });
  }, [location]);

  useEffect(() => {
    const queryParams = new URLSearchParams(getCurrentQueryParams());
    const searchParam = queryParams.get('search') || '';
    const currentPage = 1;

    setSearchTerm(searchParam);

    isLoading(true);

    runSearch(searchParam, language, currentPage, limit, typeFilter).then((res) => {
      onSearch({
        searchTerm: searchParam,
        res: res.results,
        currentPage,
        totalResults: res.totalResults,
      });

      isLoading(false);
    });
  }, [typeFilter]);

  const clearSearchTerm = () => {
    setSearchTerm('');
    history.pushState({}, 'search', '/search');
    onSearch({
      searchTerm: '',
      res: [],
      currentPage: 1,
      totalResults: 0,
    });
  };

  return (
    <>
      <form className="search-filter-bar" action="/search" onSubmit={onSubmit}>
        <div className="search-filter-bar__input-container">
          <SearchIcon className="search-filter-bar__input-icon" />

          <input
            className="search-filter-bar__search-box"
            type="text"
            name="search"
            value={searchTerm}
            onChange={onChange}
            placeholder={translateLanguage(language, 'Search the website', 'Realizar una busqueda')}
            aria-label={translateLanguage(language, 'Search the website', 'Realizar una busqueda')}
          />
          {searchTerm && (
            <button
              aria-label={translateLanguage(language, 'Clear Search', 'Borrar la Busqueda')}
              className="search-filter-bar__close-icon"
              onClick={clearSearchTerm}
              type="reset"
            >
              <CloseIcon title="Clear Search" />
            </button>
          )}
        </div>

        <button
          className="search-filter-bar__button"
          aria-label={translateLanguage(language, 'Search', 'Buscar')}
        >
          {translateLanguage(language, 'Search', 'Buscar')}
        </button>
      </form>

      {loading && <LoadingSpinner />}
    </>
  );
}
